import {
  serverZonePolygonRejection,
  validateZonePolygon,
  zoneShapeProblem,
  type ZoneAreaType,
  type ZonePolygonRejection,
} from "@produce/shared";

/**
 * What the zone editor tells a producer when a drawn shape cannot be saved.
 *
 * Two places can refuse a shape, with the same rules: validateZonePolygon
 * before the write, and the festival_areas_validate_polygon trigger
 * (SQLSTATE 22023) as the backstop. Both end up here, so the producer reads
 * the same sentence whichever one said no. Pure: no toast, no map.
 */

const AREA_LABELS: Record<ZoneAreaType, string> = {
  stage_zone: "Stage zone",
  festival_boundary: "Festival boundary",
  media_office: "Media office",
  crew_catering: "Crew catering",
};

function areaLabel(areaType: ZoneAreaType): string {
  // Own keys only: area_type is a plain string in the DB types.
  return Object.prototype.hasOwnProperty.call(AREA_LABELS, areaType)
    ? AREA_LABELS[areaType]
    : "Zone";
}

// The clause itself lives in @produce/shared, so the producer app says the
// same sentence; re-exported for the web's own callers.
export { zoneShapeProblem };

/** The toast for a refused shape. */
export function zoneShapeRefusalMessage(
  reason: ZonePolygonRejection | null,
  areaType: ZoneAreaType,
): string {
  return `${areaLabel(areaType)} not saved — ${zoneShapeProblem(reason, areaType)}. Please redraw it.`;
}

/** Checks the shape with validateZonePolygon: a toast, or null when it may be saved. */
export function zoneShapeMessage(geometry: unknown, areaType: ZoneAreaType): string | null {
  const validation = validateZonePolygon(geometry, areaType);
  return validation.ok === false ? zoneShapeRefusalMessage(validation.reason, areaType) : null;
}

/**
 * Is this write error the server refusing the shape (SQLSTATE 22023)?
 * Then `{ reason }` — null when the message names no known code. Any other
 * error: null, and the caller keeps its own handling.
 */
export function serverZoneShapeRefusal(
  error: unknown,
): { reason: ZonePolygonRejection | null } | null {
  if (typeof error !== "object" || error === null || !("code" in error)) return null;
  if (error.code !== "22023") return null;
  return { reason: serverZonePolygonRejection(error) };
}

/** A server shape refusal as a toast, or null for any other error. */
export function zoneServerErrorMessage(error: unknown, areaType: ZoneAreaType): string | null {
  const refusal = serverZoneShapeRefusal(error);
  return refusal ? zoneShapeRefusalMessage(refusal.reason, areaType) : null;
}
